import { createGlobalStyle, css } from 'styled-components';

import { fontFamily } from './theme';
import { getEnvBackgroundDeclaration } from './utils/environment';

const GlobalStyles = createGlobalStyle`
  :root {
    ${({ theme }) => css`
      --linkText: ${theme.linkText};
      --mainText: ${theme.mainText};
      --secondaryText: ${theme.secondaryText};
      --primary: ${theme.primary};
      --secondary: ${theme.secondary};
      --border: ${theme.border};
      --defaultBackground: ${theme.defaultBackground};
      --defaultTableCell: ${theme.defaultTableCell};
      --defaultShadow: ${theme.defaultShadow};
      --disabledButton: ${theme.disabledButton};
      --defaultTableCellTitle: ${theme.defaultTableCellTitle};
      --mainRequired: ${theme.mainRequired};
      --marker1: ${theme.marker1};
      --marker2: ${theme.marker2};
      --marker3: ${theme.marker3};
      --marker4: ${theme.marker4};
      --marker5: ${theme.marker5};
      --marker6: ${theme.marker6};

      --primary-50: ${theme.palette.primary50};
      --primary-100: ${theme.palette.primary100};
      --primary-200: ${theme.palette.primary200};
      --primary-300: ${theme.palette.primary300};
      --primary-400: ${theme.palette.primary400};
      --primary-500: ${theme.palette.primary500};
      --primary-600: ${theme.palette.primary600};
      --primary-700: ${theme.palette.primary700};
      --primary-800: ${theme.palette.primary800};
      --primary-900: ${theme.palette.primary900};

      --neutral-0: ${theme.palette.neutral0};
      --neutral-50: ${theme.palette.neutral50};
      --neutral-100: ${theme.palette.neutral100};
      --neutral-200: ${theme.palette.neutral200};
      --neutral-300: ${theme.palette.neutral300};
      --neutral-400: ${theme.palette.neutral400};
      --neutral-500: ${theme.palette.neutral500};
      --neutral-600: ${theme.palette.neutral600};
      --neutral-700: ${theme.palette.neutral700};
      --neutral-800: ${theme.palette.neutral800};
      --neutral-900: ${theme.palette.neutral900};

      --success-50: ${theme.palette.success50};
      --success-100: ${theme.palette.success100};
      --success-500: ${theme.palette.success500};
      --success-600: ${theme.palette.success600};
      --success-700: ${theme.palette.success700};

      --warning-50: ${theme.palette.warning50};
      --warning-100: ${theme.palette.warning100};
      --warning-500: ${theme.palette.warning500};
      --warning-600: ${theme.palette.warning600};
      --warning-700: ${theme.palette.warning700};

      --danger-50: ${theme.palette.danger50};
      --danger-100: ${theme.palette.danger100};
      --danger-500: ${theme.palette.danger500};
      --danger-600: ${theme.palette.danger600};
      --danger-700: ${theme.palette.danger700};

      --info-50: ${theme.palette.info50};
      --info-500: ${theme.palette.info500};
      --info-600: ${theme.palette.info600};

      --space-0: ${theme.space[0]};
      --space-1: ${theme.space[1]};
      --space-2: ${theme.space[2]};
      --space-3: ${theme.space[3]};
      --space-4: ${theme.space[4]};
      --space-5: ${theme.space[5]};
      --space-6: ${theme.space[6]};
      --space-8: ${theme.space[8]};
      --space-10: ${theme.space[10]};
      --space-12: ${theme.space[12]};
      --space-16: ${theme.space[16]};

      --radius-sm: ${theme.radius.sm};
      --radius-md: ${theme.radius.md};
      --radius-lg: ${theme.radius.lg};
      --radius-xl: ${theme.radius.xl};
      --radius-full: ${theme.radius.full};

      --shadow-xs: ${theme.shadow.xs};
      --shadow-sm: ${theme.shadow.sm};
      --shadow-md: ${theme.shadow.md};
      --shadow-lg: ${theme.shadow.lg};
      --shadow-focus: ${theme.shadow.focus};
      --shadow-focus-danger: ${theme.shadow.focusDanger};

      --fs-xs: ${theme.fontSize.xs};
      --fs-sm: ${theme.fontSize.sm};
      --fs-md: ${theme.fontSize.md};
      --fs-base: ${theme.fontSize.base};
      --fs-lg: ${theme.fontSize.lg};
      --fs-xl: ${theme.fontSize.xl};
      --fs-2xl: ${theme.fontSize['2xl']};
      --fs-3xl: ${theme.fontSize['3xl']};
      --fs-4xl: ${theme.fontSize['4xl']};

      --lh-tight: ${theme.lineHeight.tight};
      --lh-snug: ${theme.lineHeight.snug};
      --lh-normal: ${theme.lineHeight.normal};
      --lh-relaxed: ${theme.lineHeight.relaxed};

      --fw-regular: ${theme.fontWeight.regular};
      --fw-medium: ${theme.fontWeight.medium};
      --fw-semibold: ${theme.fontWeight.semibold};
      --fw-bold: ${theme.fontWeight.bold};

      --motion-fast: ${theme.motion.fast};
      --motion-base: ${theme.motion.base};
      --motion-slow: ${theme.motion.slow};
      --ease-standard: ${theme.motion.ease};
      --ease-out: ${theme.motion.easeOut};

      --initialZIndex: 1;
      --popperZIndex: 5;
      --modalZIndex: 10;

      /* Table row colors — change these to recolor every table at once. */
      --table-row-base: var(--neutral-0);
      --table-row-alt: var(--primary-50);
      --table-row-hover: var(--neutral-50);
      --table-row-header: var(--neutral-100);

      /* Environment tint. Dev/UAT define --app-bg (yellow/green) so QA can
         tell at a glance which deployment they're in. In production/local the
         token is left undefined, so every var(--app-bg, original) surface
         keeps its original color. See utils/environment.ts. */
      ${getEnvBackgroundDeclaration()}
    `}
  }

  html, body {
    background: var(--app-bg, var(--neutral-50));
  }

  body {
    color: var(--neutral-800);
    font-size: var(--fs-base);
    line-height: var(--lh-normal);
    margin: 0;
    padding: 0;
    font-family: ${fontFamily};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  h1, h2, h3, h4, h5, h6 {
    color: var(--neutral-900);
    font-weight: var(--fw-semibold);
    line-height: var(--lh-snug);
    margin: 0;
  }

  h1 { font-size: var(--fs-4xl); letter-spacing: -0.01em; }
  h2 { font-size: var(--fs-3xl); letter-spacing: -0.005em; }
  h3 { font-size: var(--fs-2xl); }
  h4 { font-size: var(--fs-xl); }
  h5 { font-size: var(--fs-lg); }
  h6 { font-size: var(--fs-base); }

  p { margin: 0; }

  a {
    display: inline;
    color: var(--primary-600);
    text-decoration-color: var(--primary-600);
    text-underline-offset: 2px;
    transition: color var(--motion-fast) var(--ease-standard),
      text-decoration-color var(--motion-fast) var(--ease-standard);

    &:hover, &:focus, &:active {
      color: var(--primary-700);
      text-decoration-color: transparent;
    }
  }

  :focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
    border-radius: var(--radius-sm);
  }

  input {
    &::-ms-reveal, ::-ms-clear {
      display: none;
    }
  }

  input, select, textarea {
    &[readonly] {
      color: var(--neutral-500);
      background-color: var(--neutral-100);
    }
  }

  button {
    cursor: pointer;
    font-family: inherit;

    &:disabled {
      cursor: not-allowed;
    }
  }

  ::selection {
    background: var(--primary-100);
    color: var(--primary-800);
  }

  /* Date-picker calendars are portalled to <body> (see InputDatePickerWithLabel)
     so they escape the draggable modal's transform/overflow. Lift them above the
     modal layer (--modalZIndex / nested modals at 20) so they stay visible. */
  .react-datepicker-popper.ep-datepicker-popper {
    z-index: 99999;
  }

  /* Calendar nav-arrow styling for portalled pickers (was scoped in
     DatePickerControl before the calendar moved to a <body> portal). */
  .ep-datepicker-popper .react-datepicker__navigation--previous {
    left: 10px;
    border: 0.45rem solid transparent;
    border-right-color: #ccc;
  }
  .ep-datepicker-popper .react-datepicker__navigation--next {
    border: 0.45rem solid transparent;
    border-left-color: #ccc;
  }

  /* subtle scrollbars on scrollable panels */
  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  ::-webkit-scrollbar-thumb {
    background: var(--neutral-300);
    border-radius: var(--radius-full);
    border: 2px solid transparent;
    background-clip: padding-box;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: var(--neutral-400);
    background-clip: padding-box;
    border: 2px solid transparent;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
`;

export default GlobalStyles;
